import { EditOutlined, DeleteFilled } from '@ant-design/icons';
import { Button, Checkbox, Form, Input, InputNumber, message, Modal, Popconfirm, Space, Table } from 'antd';
import { PageHeader } from "@ant-design/pro-layout";
import Search from 'antd/lib/input/Search';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { addBrand, addBrandData, deleteBrand, deleteBrandData, searchBrands, updateBrand } from 'services';

interface Item {
  key: string;
  name: string;
  slug: string;
  riskLevel: number | null;
  note: string | null;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: 'number' | 'text' | 'checkbox';
  record: any;
  index: number;
  children: React.ReactNode;
  checkedValue: boolean;
  onChange: (evt: any) => void;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  checkedValue,
  onChange,
  ...restProps
}) => {
  const inputNode = () => {
    switch (inputType) {
      case "number":
        return <InputNumber />
      case "checkbox":
        return <Checkbox checked={checkedValue} onChange={onChange} />
      default:
        return <Input />
    }
  }

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={dataIndex === "name" ? [
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ] : []}
        >
          {inputNode()}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const Brands = () => {
  const [form] = Form.useForm();
  const [form1] = Form.useForm();
  const [form2] = Form.useForm();
  const [data, setData] = useState<any>([]);
  const [editingKey, setEditingKey] = useState('');
  const [loading, setLoading] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>('')
  const [newBrandName, setNewBrandName] = useState<string>('');
  const [loadingElement, setLoadingElement] = useState<any>({
    element: '',
    loading: false,
  })
  const [openModalAddData, setOpenModalAddData] = useState<boolean>(false);
  const [editingField, setEditingField] = useState<any>({
    type: "",
    source: ""
  });
  const [checkboxValue, setCheckboxValue] =  useState<boolean>(false);

  const expandedRowRender = (record: any) => {
    const columns = [
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        editable: true,
      },
      {
        title: 'Source',
        dataIndex: 'source',
        key: 'source',
        editable: true,
      },
      {
        title: 'Last reported',
        dataIndex: 'lastReported',
        key: 'lastReported',
        editable: true,
      },
      {
        title: 'Times report',
        dataIndex: 'timeReport',
        key: 'timeReport',
        editable: true,
      },
      {
        title: 'Marketplace',
        dataIndex: 'marketplace',
        key: 'marketplace',
        editable: true,
      },
      {
        title: 'Condition of Claim',
        dataIndex: 'conditionOfClaim',
        key: 'conditionOfClaim',
        editable: true,
      },
      {
        title: 'Verified',
        dataIndex: 'verified',
        key: 'verified',
        editable: true,
        render: (value: any) => value ? "Yes" : "No"
      },
      {
        title: 'Note',
        dataIndex: 'note',
        key: 'note',
        editable: true,
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_: any, item: any) => {
          const editable = isEditing(item);
  
          return (
            <Space size="large">
              {editable ? (
                <Space>
                  <Button onClick={() => handleOnUpdateData(record._id)} type="primary">Save</Button>
                  <Popconfirm title="Sure to cancel?" onConfirm={handleOnCancel}>
                    <Button>Cancel</Button>
                  </Popconfirm>
                </Space>
              ) : (
                <Button icon={<EditOutlined />}  type="primary" disabled={editingKey !== ''} onClick={() => edit(item)} />
              )}
              <Popconfirm
                placement="bottomRight"
                title="Are you sure to delete this data?"
                onConfirm={() => handleOnDeleteData(item)}
                okText="Yes"
                cancelText="No"
              >
                <Button type="primary" icon={<DeleteFilled />} danger />
              </Popconfirm>
            </Space>
          )
        }
      },
    ];
    const data: any = [];

    for (let i = 0; i < record.data.length; ++i) {
      data.push({
        key: `data-${i}-${record._id}`,
        brandId: record._id,
        ...record.data[i]
      });
    }

    const edit = (r?: any) => {
      form1.setFieldsValue(r);
      setEditingField(() => {
        setEditingKey(r.key);
        setCheckboxValue(r.verified);
        return ({
          type: r.type,
          source: r.source
        });
      });
    };

    const handleOnAddData = (brandId: React.Key, values: any) => {
      setLoading(true);
      const params = {
        brandId,
        type: values.type || "",
        source: values.source || "",
        timeReport: values.timeReport || "",
        lastReported: values.lastReported,
        verified: values.verified ? '1' : '0',
        note: values.note || "",
        marketplace: values.marketplace || "",
        conditionOfClaim: values.conditionOfClaim || "",
      }
      addBrandData(params)
        .then(() => {
          setOpenModalAddData(false);
          loadBrands();
        })
        .catch(() => {
          message.error("Can not add brand")
        })
        .finally(() => setLoading(false))
    };
  
    const handleOnUpdateData = async (brandId: string) => {
      setLoading(true);
      try {
        const row = (await form1.getFieldsValue());

        const params = {
          brandId,
          type: row.type,
          source: row.source,
          timeReport: row.timeReport,
          lastReported: row.lastReported,
          verified: checkboxValue ? '1' : '0',
          note: row.note,
          marketplace: row.marketplace,
          conditionOfClaim: row.conditionOfClaim
        }

        deleteBrandData({ brandId, ...editingField })
          .then(() => {
            addBrandData(params)
              .then(() => {
                message.success("Edit data successfully")
                setEditingKey('');
                loadBrands();
              })
              .catch(() => {
                message.error("Can not update brand")
              })
              .finally(() => setLoading(false))
            })
      } catch (errInfo) {
        setLoading(false)
        console.log('Validate Failed:', errInfo);
      }
    };

    const handleOnDeleteData = (record: any) => {
      setLoading(true)
      deleteBrandData(record)
        .then(() => {
          message.success("Delete data successfully")
          loadBrands()
        })
        .catch(() => message.error("Can not delete data"))
        .finally(() => setLoading(false))
    };

    const mergedChildColumns = columns.map(col => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record: any) => ({
          record,
          inputType: col.dataIndex === 'verified' ? 'checkbox' : 'text',
          dataIndex: col.dataIndex,
          title: col.title,
          editing: isEditing(record),
        }),
      };
    });

    return (
      <>
        <Form form={form1} component={false}>
          <Table
            key={`table-${record._id}-children`}
            columns={mergedChildColumns}
            components={{
              body: {
                cell: (props: any) => EditableCell({ ...props, checkedValue: checkboxValue, onChange: (e) => setCheckboxValue(e.target.checked) }),
              },
            }}
            dataSource={data}
            footer={() => <Button onClick={() => setOpenModalAddData(true)}>Add</Button>}
            pagination={false}
            size="small"
          />
        </Form>
        <Modal
          visible={openModalAddData}
          onCancel={() => setOpenModalAddData(false)}
          onOk={() => {
            form2
              .validateFields()
              .then(values => {
                form2.resetFields();
                handleOnAddData(record._id, values);
              })
              .catch(info => {
                console.log('Validate Failed:', info);
              });
          }}
          closable={false}
          confirmLoading={loading}
        >
          <Form
            form={form2}
            name="basic"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            initialValues={{ verified: true }}
            autoComplete="off"
          >
            <Form.Item
              label="Type"
              name="type"
              rules={[{ required: true, message: 'Please input Type!' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Source"
              name="source"
              rules={[{ required: true, message: 'Please input Source!' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Last reported"
              name="lastReported"
              rules={[{ required: true, message: 'Please input Last reported!' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Times report"
              name="timesReport"
              rules={[{ required: true, message: 'Please input Times report!' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Marketplace"
              name="marketplace"
              rules={[{ required: true, message: 'Please input Marketplace!' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Condition of Claim"
              name="conditionOfClaim"
            >
              <Input />
            </Form.Item>

            <Form.Item name="verified" valuePropName="verified" wrapperCol={{ offset: 8, span: 16 }}>
              <Checkbox>Verified</Checkbox>
            </Form.Item>

            <Form.Item
              label="Note"
              name="note"
            >
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      </>
    );
  };

  const columns = [
    {
      title: 'Name',
      width: 200,
      dataIndex: 'name',
      key: 'name',
      editable: true,
    },
    {
      title: 'Slug',
      width: 150,
      dataIndex: 'slug',
      key: 'slug',
    },
    {
      title: 'Risk Level',
      width: 100,
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      editable: true,
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      editable: true,
    },
    {
      title: 'Actions',
      width: 200,
      key: 'actions',
      render: (_: any, record: any) => {
        const editable = isEditing(record);

        return (
          <Space size="large">
            {editable ? (
              <Space>
                <Button onClick={() => handleOnUpdateBrand(record.key)} type="primary">Save</Button>
                <Popconfirm title="Sure to cancel?" onConfirm={handleOnCancel}>
                  <Button>Cancel</Button>
                </Popconfirm>
              </Space>
            ) : (
              <Button icon={<EditOutlined />}  type="primary" disabled={editingKey !== ''} onClick={() => edit(record)} />
            )}
            <Popconfirm
              placement="bottomRight"
              title="Are you sure to delete this brand?"
              onConfirm={() => handleOnDeleteBrand(record.key)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary" icon={<DeleteFilled />} danger />
            </Popconfirm>
          </Space>
        )
      }
    },
  ];

  const isEditing = (record: Item) => record.key === editingKey;

  const edit = (record: Partial<Item> & { key: React.Key }) => {
    form.setFieldsValue(record);
    setEditingKey(record.key);
  };

  const handleOnDeleteBrand = (id: string) => {
    setLoading(true);
    deleteBrand(id)
      .then(() => {
        message.success('Delete brand successfully')
        const newData = data.filter((item: any) => id !== item.key);
        setData(newData);
      })
      .catch(() => message.error('Can not delete brand'))
      .finally(() => setLoading(false))
  }

  const handleOnCancel = () => {
    setEditingKey('');
  };

  const handleOnUpdateBrand = async (key: React.Key) => {
    setLoading(true);
    try {
      (await form.validateFields(["name"])) as Item
      const row = (await form.getFieldsValue()) as Item;

      const params = {
        id: key,
        name: row.name,
        note: row.note || null,
        riskLevel: row.riskLevel || null
      }
      updateBrand(params)
        .then(() => {
          message.success("Update brand successfully")
          const newData = [...data];
          const index = newData.findIndex(item => key === item.key);
          if (index > -1) {
            const item = newData[index];
            newData.splice(index, 1, {
              ...item,
              ...params,
            });
            setData(newData);
            setEditingKey('');
          }
        })
        .catch(() => {
          message.error("Can not update brand")
        })
        .finally(() => setLoading(false))

    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const mergedColumns = columns.map(col => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: Item) => ({
        record,
        inputType: col.dataIndex === 'riskLevel' ? 'number' : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const loadBrands = (params: any = {}) => {
    setLoading(true);
    searchBrands(params)
      .then(({ data: { data: res } }: any) => {
        setData(() => res.brands.map((b: any) => ({ key: b._id, ...b })))
      })
      .catch(() => message.error("Can not load brands"))
      .finally(() => setLoading(false));
  }

  const handleOnSearch = (value: string, evt: any) => {
    setLoading(true);
    const params = { search: decodeURI(value) };
    loadBrands({ params });
  }

  const handleOnAddBrand = () => {
    setLoadingElement({
      element: 'addBrandBtn',
      loading: true
    });
    const params = { name: newBrandName };
    addBrand(params)
      .then(({ data: { data: res } }) => {
        message.success('Added new brand successfully');
        setData((prev: any) => [{ key: res._id, ...res }, ...prev])
      })
      .catch(() => message.error('Can not add new brand'))
      .finally(() => setLoadingElement({ element: '', loading: false }))
  }

  useEffect(() => {
    loadBrands();
  }, [])

  return (
    <>
      <Helmet>
        <title>Brands | Admin UI</title>
      </Helmet>
      <PageHeader
        className="page-header"
        title="Brands"
        footer={
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Form form={form} component={false}>
              <Table
                bordered
                columns={mergedColumns}
                components={{
                  body: {
                    cell: EditableCell,
                  },
                }}
                dataSource={data}
                expandable={{
                  expandedRowRender,
                }}
                loading={loading}
                title={() =>
                  <Space size="large">
                    <Search
                      enterButton
                      placeholder="Search name"
                      value={keyword}
                      onChange={(e: any) => setKeyword(e.target.value)}
                      onSearch={(value: string, evt: any) => {
                        const kw = value.trim();
                        setKeyword(kw);
                        handleOnSearch(kw, evt);
                      }}
                      style={{ width: 400 }}
                      loading={loading}
                    />
                    <Input.Group compact>
                      <Input
                        value={newBrandName}
                        disabled={loadingElement.element === "addBrandBtn" && loadingElement.loading}
                        onChange={(e) => setNewBrandName(e.target.value)}
                        style={{ width: 200 }}
                      />
                      <Button
                        type="primary"
                        loading={loadingElement.element === "addBrandBtn" && loadingElement.loading}
                        onClick={handleOnAddBrand}
                      >
                        Add Brand
                      </Button>
                    </Input.Group>
                  </Space>
                }
                size="middle"
              />
            </Form>
          </Space>
        }
      />
    </>
  );
};

export default Brands;